import { useCallback, useEffect, useState } from "react";
import Link from "next/link";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
} from "@mui/material";

import { SkillInstance } from "../../../../src/API";

import { usePlatformSettings } from "../../../../providers/platformSettings";
import { useProfile } from "../../../../providers/profile";
import { useSkillInstances } from "../../../../providers/state/skillInstance";
import HoverRating from "../hover-rating";

const SkillInstanceDetails = ({
  instance,
  refresh,
}: {
  instance: Partial<SkillInstance>;
  refresh: () => void;
}) => {
  const [score, setScore] = useState<number | null>(0);

  const { toggleActive, toggleComplete, toggleDelete, updateScore } =
    useSkillInstances();
  const { instruments } = usePlatformSettings();
  const { account } = useProfile();

  useEffect(() => {
    if (instance.percentage) setScore(instance.percentage / 20);
  }, [instance]);

  const onSetScore = useCallback(
    (newScore: number | null) => {
      setScore(newScore);
      updateScore(instance, newScore);
    },
    [instance, updateScore]
  );

  return (
    <Card>
      <CardHeader
        title="SKILL DETAILS"
        sx={{ "& span": { fontSize: "1rem" } }}
        subheader={
          <Stack direction="row" spacing={1} sx={{ marginTop: "0.5rem" }}>
            {instance.isDeleted ? (
              <Chip
                label="DELETED"
                color="error"
                size="small"
                sx={{
                  span: {
                    fontSize: "0.8rem",
                  },
                }}
              />
            ) : null}
            {instance.isInactive ? (
              <Chip
                label="INACTIVE"
                color="warning"
                size="small"
                sx={{
                  span: {
                    fontSize: "0.8rem",
                  },
                }}
              />
            ) : null}
            {instance.isCompleted ? (
              <Chip
                label="COMPLETED"
                color="success"
                size="small"
                sx={{
                  span: {
                    fontSize: "0.8rem",
                  },
                }}
              />
            ) : null}
          </Stack>
        }
      />
      <CardContent>
        <div
          className="grid gap-4 items-center"
          style={{ gridTemplateColumns: "1fr 3fr 1fr" }}
        >
          <h4 className="font-bold">Title:</h4>
          <div>{instance.template?.name}</div>
          <div></div>
          <h4 className="font-bold">Instrument:</h4>
          <div>
            {
              instruments?.find((i) => i.tag === instance.template?.instrument)
                ?.label
            }
          </div>
          <div></div>
          <h4 className="font-bold">Category:</h4>
          <div>{instance.template?.category}</div>
          <div></div>
          <h4 className="font-bold">Description:</h4>
          <div>{instance.template?.description}</div>
          <div></div>
          <div className="text-center" style={{ gridColumn: "1 / span 2" }}>
            <div className="flex items-center">
              <HoverRating
                value={score}
                setValue={onSetScore}
                readOnly={
                  !account?.roles?.some((r) =>
                    ["teacher", "admin", "superadmin"].includes(r as string)
                  )
                }
              />
            </div>
          </div>
          <div></div>
          {account?.roles?.some((r) =>
            ["teacher", "admin", "superadmin"].includes(r as string)
          ) ? (
            <Box
              sx={{
                display: "flex",
                gap: "1rem",
                flexWrap: "wrap",
                gridColumn: "1 / span 3",
              }}
            >
              <Link href={`/teacher/skill-template/${instance.template?.id}`}>
                <Button color="secondary" size="small" variant="outlined">
                  To Template
                </Button>
              </Link>
              <Button
                color="secondary"
                size="small"
                variant="outlined"
                onClick={async () => {
                  await toggleComplete(instance);
                  refresh();
                }}
              >
                {instance.isCompleted ? "Incomplete" : "Complete"}
              </Button>
              <Button
                color="secondary"
                size="small"
                variant="outlined"
                onClick={async () => {
                  await toggleActive(instance);
                  refresh();
                }}
              >
                {instance.isInactive ? "Active" : "Inactive"}
              </Button>
              <Button
                color="secondary"
                size="small"
                variant="outlined"
                onClick={async () => {
                  await toggleDelete(instance);
                  refresh();
                }}
              >
                {instance.isDeleted ? "Restore" : "Delete"}
              </Button>
            </Box>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default SkillInstanceDetails;
